// AuthContext — top-level provider that holds the current user, exposes
// login/register/logout/refreshToken actions, and keeps localStorage in sync
// with in-memory state.
//
// Hydration on mount:
//   1. Read access + refresh tokens from localStorage.
//   2. If both exist, call GET /api/auth/me. apiFetch will attach the bearer
//      and, on a 401, attempt one transparent refresh. If refresh fails,
//      apiFetch fires `ss.auth.signedOut` and clears tokens — we listen for
//      that event and drop in-memory state too.
//   3. If /me succeeds, populate `user` and the in-memory accessToken.
//   4. If /me fails (and refresh didn't recover), drop tokens and stay
//      signed out.

import * as React from 'react';
import { authService, type AuthUser, type AuthSuccessResponse } from '@/services/authService';
import {
  ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  SIGNED_OUT_EVENT,
} from '@/auth/apiClient';

interface AuthContextValue {
  user: AuthUser | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<AuthUser>;
  register: (email: string, password: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<AuthUser | null>;
}

const AuthContext = React.createContext<AuthContextValue | undefined>(undefined);

function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string | null) {
  try {
    if (value == null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* swallow */
  }
}

function persistTokens(access: string, refresh: string) {
  writeStored(ACCESS_TOKEN_KEY, access);
  writeStored(REFRESH_TOKEN_KEY, refresh);
}

function clearTokens() {
  writeStored(ACCESS_TOKEN_KEY, null);
  writeStored(REFRESH_TOKEN_KEY, null);
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = React.useState<AuthUser | null>(null);
  const [accessToken, setAccessToken] = React.useState<string | null>(() => readStored(ACCESS_TOKEN_KEY));
  const [isLoading, setIsLoading] = React.useState<boolean>(true);

  // Hydration: on mount, if tokens exist, verify by calling /me. apiFetch
  // will refresh transparently on a 401; if both fail, it fires
  // ss.auth.signedOut, which we handle below.
  React.useEffect(() => {
    let cancelled = false;
    const access = readStored(ACCESS_TOKEN_KEY);
    const refresh = readStored(REFRESH_TOKEN_KEY);
    if (!access || !refresh) {
      setIsLoading(false);
      return;
    }
    (async () => {
      try {
        const me = await authService.me();
        if (cancelled) return;
        // After /me, the access token may have been refreshed by apiClient,
        // so re-read from storage.
        setAccessToken(readStored(ACCESS_TOKEN_KEY));
        setUser(me);
      } catch {
        if (cancelled) return;
        clearTokens();
        setAccessToken(null);
        setUser(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Listen for forced sign-outs from apiClient (refresh-failure path).
  React.useEffect(() => {
    const onSignedOut = () => {
      setAccessToken(null);
      setUser(null);
    };
    window.addEventListener(SIGNED_OUT_EVENT, onSignedOut);
    return () => window.removeEventListener(SIGNED_OUT_EVENT, onSignedOut);
  }, []);

  // Cross-tab sync: when another tab logs in/out, react.
  React.useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === ACCESS_TOKEN_KEY) {
        setAccessToken(e.newValue);
        if (!e.newValue) setUser(null);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const applyAuthSuccess = React.useCallback((res: AuthSuccessResponse) => {
    persistTokens(res.accessToken, res.refreshToken);
    setAccessToken(res.accessToken);
    setUser(res.user);
    return res.user;
  }, []);

  const login = React.useCallback(
    async (email: string, password: string) => {
      const res = await authService.login(email, password);
      return applyAuthSuccess(res);
    },
    [applyAuthSuccess],
  );

  const register = React.useCallback(
    async (email: string, password: string) => {
      const res = await authService.register(email, password);
      return applyAuthSuccess(res);
    },
    [applyAuthSuccess],
  );

  const logout = React.useCallback(async () => {
    const refresh = readStored(REFRESH_TOKEN_KEY);
    try {
      if (refresh) await authService.logout(refresh);
    } catch {
      /* swallow — clear local state regardless */
    } finally {
      clearTokens();
      setAccessToken(null);
      setUser(null);
    }
  }, []);

  const refreshToken = React.useCallback(async () => {
    const refresh = readStored(REFRESH_TOKEN_KEY);
    if (!refresh) {
      clearTokens();
      setAccessToken(null);
      setUser(null);
      return null;
    }
    try {
      const res = await authService.refresh(refresh);
      applyAuthSuccess(res);
      return res.user;
    } catch {
      clearTokens();
      setAccessToken(null);
      setUser(null);
      return null;
    }
  }, [applyAuthSuccess]);

  const value = React.useMemo<AuthContextValue>(
    () => ({
      user,
      accessToken,
      isAuthenticated: !!user && !!accessToken,
      isLoading,
      login,
      register,
      logout,
      refreshToken,
    }),
    [user, accessToken, isLoading, login, register, logout, refreshToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

export default AuthProvider;
